import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table } from '../components/ui/Table';
import { SearchFilter } from '../components/ui/SearchFilter';
import { Card } from '../components/ui/Card';
import { User, Smartphone, ShoppingCart } from 'lucide-react';
import { getEsimCustomers, userProfile } from '../services/api';

export const UserManagement: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [activeTab, setActiveTab] = useState<'profile' | 'esims' | 'orders'>('profile');
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);

  // Load users from API
  const loadUsers = async () => {
    setLoading(true);
    try {
      const result = await getEsimCustomers(searchTerm || undefined, page, pageSize);
      if (result && result.data) {
        setUsers(result.data);
        setTotal(result.pagination?.total || 0);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [page, pageSize, searchTerm]);
  // Table columns
  const columns = [{
    key: 'customer_name',
    header: 'Name'
  }, {
    key: 'email_id',
    header: 'Email'
  }, {
    key: 'last_order_date',
    header: 'Last Order Date',
    render: (value: any) => value ? new Date(value).toLocaleDateString() : '-'
  }, {
    key: 'order_count',
    header: 'Orders'
  }, {
    key: 'actions',
    header: 'Actions',
    render: (_: any, row: any) => <button onClick={() => handleViewUser(row)} className="text-indigo-600 hover:text-indigo-900">
          View Profile
        </button>
  }];

  // Handle view user
  const handleViewUser = async (user: any) => {
    try {
      const result = await userProfile(user.email_id);
      if (result && result.email) {
        setSelectedUser({
          ...result,
          customer_name: user.customer_name,
          customer_email: user.email_id
        });
        setActiveTab('profile');
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };
  // Go back to user list
  const handleBackToList = () => {
    setSelectedUser(null);
  };
  return <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-800">
          User Management
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          View and manage user accounts
        </p>
      </div>
      {selectedUser ? <div className="space-y-6">
          {/* Back button */}
          <button onClick={handleBackToList} className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
            &larr; Back to User List
          </button>
          {/* User profile card */}
          <Card>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between border-b border-gray-200 pb-4 mb-4">
              <div className="flex items-center">
                <div className="h-12 w-12 rounded-full bg-indigo-100 flex items-center justify-center">
                  <User className="h-6 w-6 text-indigo-600" />
                </div>
                <div className="ml-4">
                  <h2 className="text-xl font-medium text-gray-900">
                    {selectedUser.customer_name || selectedUser.full_name || selectedUser.email}
                  </h2>
                  <p className="text-sm text-gray-500">{selectedUser.customer_email || selectedUser.email}</p>
                </div>
              </div>
              <div className="mt-4 md:mt-0 flex space-x-2">
                <button className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                  Send Email
                </button>
              </div>
            </div>
            {/* Tabs */}
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                <button className={`${activeTab === 'profile' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} onClick={() => setActiveTab('profile')}>
                  Profile
                </button>
                <button className={`${activeTab === 'esims' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} onClick={() => setActiveTab('esims')}>
                  E-SIMs ({selectedUser.profiles?.length || 0})
                </button>
                <button className={`${activeTab === 'orders' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} onClick={() => setActiveTab('orders')}>
                  Orders ({selectedUser.orders?.length || 0})
                </button>
              </nav>
            </div>
            {/* Tab Content */}
            <div className="py-4">
              {activeTab === 'profile' && <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">
                      User Information
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Full Name
                        </p>
                        <p className="mt-1 text-sm text-gray-900">
                          {selectedUser.customer_name || selectedUser.full_name || selectedUser.email}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Email Address
                        </p>
                        <p className="mt-1 text-sm text-gray-900">
                          {selectedUser.customer_email || selectedUser.email}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Phone Number
                        </p>
                        <p className="mt-1 text-sm text-gray-900">
                          {selectedUser.phone || '-'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Address
                        </p>
                        <p className="mt-1 text-sm text-gray-900">
                          {selectedUser.address || '-'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Country
                        </p>
                        <p className="mt-1 text-sm text-gray-900">
                          {selectedUser.country || '-'}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">
                      Account Information
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          User ID
                        </p>
                        <p className="mt-1 text-sm text-gray-900">
                          {selectedUser.customer || selectedUser.email}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Registered Date
                        </p>
                        <p className="mt-1 text-sm text-gray-900">
                          {selectedUser.creation || '-'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Last Active
                        </p>
                        <p className="mt-1 text-sm text-gray-900">
                          {selectedUser.modified || '-'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          E-SIMs
                        </p>
                        <p className="mt-1 text-sm text-gray-900">
                          {selectedUser.profiles?.length || 0}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Orders
                        </p>
                        <p className="mt-1 text-sm text-gray-900">
                          {selectedUser.orders?.length || 0}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>}
              {activeTab === 'esims' && <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      E-SIMs
                    </h3>
                    <button 
                      onClick={() => navigate('/topup')} 
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Top-up E-SIM
                    </button>
                  </div>
                  <div className="space-y-4">
                    {selectedUser.profiles && selectedUser.profiles.length > 0 ? (
                      selectedUser.profiles.map((profile: any) => <div key={profile.iccid} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex justify-between items-start">
                          <div className="flex items-start">
                            <Smartphone className="h-5 w-5 text-indigo-600 mt-0.5" />
                            <div className="ml-3">
                              <p className="text-sm font-medium text-gray-900">
                                {profile.iccid}
                              </p>
                              <p className="text-xs text-gray-500">
                                Plan: {profile.plan_title}
                              </p>
                            </div>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${profile.status === 'Activated' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                            {profile.status}
                          </span>
                        </div>
                        <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <p className="text-gray-500">Activation Date</p>
                            <p className="font-medium">
                              {profile.activation_date || '-'}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500">Last Top-up</p>
                            <p className="font-medium">
                              {profile.last_topup_date || '-'}
                            </p>
                          </div>
                        </div>
                        <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <p className="text-gray-500">Data</p>
                            <p className="font-medium">
                              {profile.plan_data_mb} MB
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500">Validity</p>
                            <p className="font-medium">
                              {profile.plan_validity_days} days
                            </p>
                          </div>
                        </div>
                      </div>)
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        No E-SIMs found for this user
                      </div>
                    )}
                  </div>
                </div>}
              {activeTab === 'orders' && <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Order History
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Order ID
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Package
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Price
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Payment
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {selectedUser.orders && selectedUser.orders.length > 0 ? (
                          selectedUser.orders.map((order: any) => <tr key={order.name} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-600">
                              {order.name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {order.creation}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {order.package_name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              €{order.sales_order_total}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              {(() => {
                                // Show "FREE" if is_free_esim is true, otherwise use custom_payment_type
                                const displayValue = order.is_free_esim ? 'Free' : (order.custom_payment_type || '');
                                if (!displayValue) {
                                  return <span className="text-gray-500">-</span>;
                                }
                                return (
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    displayValue === 'Card' ? 'bg-blue-100 text-blue-800' :
                                    displayValue === 'MaMs' ? 'bg-purple-100 text-purple-800' :
                                    displayValue === 'Free' ? 'bg-green-100 text-green-800' :
                                    displayValue === 'Support' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {displayValue}
                                  </span>
                                );
                              })()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${order.status === 'Active' ? 'bg-green-100 text-green-800' : order.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                                {order.status}
                              </span>
                            </td>
                          </tr>)
                        ) : (
                          <tr>
                            <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                              No orders found for this user
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>}
            </div>
          </Card>
        </div> : <Card>
          <SearchFilter placeholder="Search by name or email..." onSearch={setSearchTerm} onFilter={() => {}} />
          <Table columns={columns} data={users} isLoading={loading} emptyMessage="No users found matching your search criteria." />
        </Card>}
    </div>;
};